// @ts-nocheck

// Save a list of named combobox actions, for future readability
const SelectActions = {
    Close: 0,
    CloseSelect: 1,
    First: 2,
    Last: 3,
    Next: 4,
    Open: 5,
    PageDown: 6,
    PageUp: 7,
    Previous: 8,
    Select: 9,
    Type: 10,
};

/*
 * Helper functions
 */

/**
 * filter an array of options against an input string
 * @param {Option[]} options
 * @param {string} filter
 * @param {string[]} exclude
 * @returns {Option[]} an array of options that begin with the filter string, case-independent
 */
function filterOptions(options = [], filter, exclude = []) {
    return options.filter((option) => {
        const matches =
            !option.disabled &&
            option.text.trim().toLowerCase().indexOf(filter.toLowerCase()) ===
                0;
        return matches && exclude.indexOf(option.text) < 0;
    });
}

/**
 * @param {KeyboardEvent} event
 * @param {boolean} menuOpen
 * @returns
 */
function getActionFromKey(event, menuOpen) {
    const { key, altKey, ctrlKey, metaKey } = event;
    const openKeys = ["ArrowDown", "ArrowUp", "Enter", " "]; // all keys that will do the default open action
    // handle opening when closed
    if (!menuOpen && openKeys.includes(key)) {
        return SelectActions.Open;
    }

    // home and end move the selected option when open or closed
    if (key === "Home") {
        return SelectActions.First;
    }
    if (key === "End") {
        return SelectActions.Last;
    }

    // handle typing characters when open or closed
    if (
        key === "Backspace" ||
        key === "Clear" ||
        (key.length === 1 && key !== " " && !altKey && !ctrlKey && !metaKey)
    ) {
        return SelectActions.Type;
    }

    // handle keys when open
    if (menuOpen) {
        if (key === "ArrowUp" && altKey) {
            return SelectActions.CloseSelect;
        } else if (key === "ArrowDown" && !altKey) {
            return SelectActions.Next;
        } else if (key === "ArrowUp") {
            return SelectActions.Previous;
        } else if (key === "PageUp") {
            return SelectActions.PageUp;
        } else if (key === "PageDown") {
            return SelectActions.PageDown;
        } else if (key === "Escape") {
            return SelectActions.Close;
        } else if (key === "Enter" || key === " ") {
            return SelectActions.CloseSelect;
        }
    }
}

/**
 * return the index of an option from an array of options, based on a search string
 * if the filter is multiple iterations of the same letter (e.g "aaa"), then cycle through first-letter matches
 * @param {Option[]} options
 * @param {string} filter
 * @param {number} startIndex
 * @returns
 */
function getIndexByLetter(options, filter, startIndex = 0) {
    const orderedOptions = [
        ...options.slice(startIndex),
        ...options.slice(0, startIndex),
    ];
    /** @type {Option | undefined} */
    const firstMatch = filterOptions(orderedOptions, filter)[0];
    /**
     * @param {string[]} array
     * @returns {boolean}
     */
    const allSameLetter = (array) =>
        array.every((letter) => letter === array[0]);

    // first check if there is an exact match for the typed string
    if (firstMatch) {
        return options.indexOf(firstMatch);
    }

    // if the same letter is being repeated, cycle through first-letter matches
    else if (allSameLetter(filter.split(""))) {
        const matches = filterOptions(orderedOptions, filter[0]);
        return options.indexOf(matches[0]);
    }

    // if no matches, return -1
    else {
        return -1;
    }
}

/**
 * check if element is visible in browser view port
 * @param {Element} element
 * @returns
 */
function isElementInView(element) {
    var bounding = element.getBoundingClientRect();

    return (
        bounding.top >= 0 &&
        bounding.left >= 0 &&
        bounding.bottom <=
            (window.innerHeight || document.documentElement.clientHeight) &&
        bounding.right <=
            (window.innerWidth || document.documentElement.clientWidth)
    );
}

/**
 * check if an element is currently scrollable
 * @param {Element} element
 * @returns
 */
function isScrollable(element) {
    return element && element.clientHeight < element.scrollHeight;
}

/**
 * Данные опции селектора
 * @typedef {Object} Option
 * @property {string | undefined} value - html атрибут value
 * @property {string} text - текстовое содержимое
 * @property {boolean} disabled - html аттрибут disabled
 */

/*
 * Select Component
 * Accepts a combobox element and an array of string options
 */
export class CustomSelect {
    /**
     * @param {HTMLDivElement} el
     */
    constructor(el) {
        // element refs
        /** @type {HTMLDivElement} */
        this._el = el;

        this._labelEl = /** @type {HTMLDivElement?} */ el.querySelector(
            ".custom-select__label",
        );

        this._comboEl =
            /** @type {HTMLDivElement} */ el.querySelector("[role=combobox]");
        this._comboTextEl =
            /** @type {HTMLElement} */ this._comboEl.querySelector(
                ".custom-select__input-text",
            );

        this._listboxEl =
            /** @type {HTMLDivElement} */ el.querySelector("[role=listbox]");

        this._optionElems = /** @type {HTMLElement[]} */ [
            ...this._listboxEl.querySelectorAll("[role=option]"),
        ];

        this._inputEl =
            /** @type {HTMLInputElement} */ el.querySelector("input");
        customSelectMap.set(this._inputEl, this);

        // data
        /** @type {string} */
        this._idBase = this._inputEl.id || "combo";
        /** @type {Option[]} */
        this._options = [];

        // state
        /** @type {number} */
        this._activeIndex = -1;
        /** @type {number} */
        this._selectedIndex = -1;
        /** @type {boolean} */
        this._open = false;
        /** @type {string} */
        this._searchString = "";
        /** @type {number | undefined} */
        this._searchTimeout = undefined;

        // init
        this._init();

        this._onResizeOrScroll = this._onResizeOrScroll.bind(this);
    }

    _init() {
        // add event listeners
        if (this._labelEl) {
            this._labelEl.addEventListener(
                "click",
                this._onLabelClick.bind(this),
            );
        }
        this._comboEl.addEventListener("blur", this._onComboBlur.bind(this));
        this._listboxEl.addEventListener(
            "focusout",
            this._onComboBlur.bind(this),
        );
        this._comboEl.addEventListener("click", this._onComboClick.bind(this));
        this._comboEl.addEventListener(
            "keydown",
            this._onComboKeyDown.bind(this),
        );

        /** @type {number | undefined} */
        let selectedIndex = undefined;

        // init options
        for (let index = 0; index < this._listboxEl.children.length; ++index) {
            const optionEl =
                /** @type {HTMLElement} */ this._listboxEl.children[index];
            this._options.push({
                value: optionEl.dataset.value,
                text: optionEl.innerText,
                disabled: optionEl.dataset.disabled === "true",
            });

            // Сохраняем индекс последнего элемента, помеченного selected
            if (optionEl.dataset.selected === "true") {
                selectedIndex = index;
            }

            optionEl.id = `${this._idBase}-${index}`;

            optionEl.addEventListener("click", (event) => {
                event.stopPropagation();
                this._onOptionClick(index);
            });
            optionEl.addEventListener(
                "mousedown",
                this._onOptionMouseDown.bind(this),
            );
        }

        // Если было передано выбранное значение, то оно имеет приоритет
        // над опциями, помеченными selected
        const selectedValue = this._comboEl.dataset.value;
        if (selectedValue !== undefined) {
            const index = this._options.findIndex(
                (opt) => opt.value === selectedValue,
            );
            if (index >= 0) {
                selectedIndex = index;
            }
        }

        /**
         * Выбор опции при инициализации
         * @param {number} index
         */
        const initOption = (index) => {
            this._onOptionChange(index);
            this._selectOption(index, false);
        };

        const placeholder = this._comboEl.dataset.placeholder;
        // select option
        if (selectedIndex !== undefined) {
            initOption(selectedIndex);
        } else if (placeholder) {
            this._comboTextEl.innerHTML = placeholder;
        } else {
            const firstNotDisabled = this._getFirstNotDisabledIndex();
            if (firstNotDisabled >= 0) {
                initOption(firstNotDisabled);
            }
        }
    }

    /**
     * @param {string} char
     */
    _getSearchString(char) {
        // reset typing timeout and start new timeout
        // this allows us to make multiple-letter matches, like a native select
        if (typeof this._searchTimeout === "number") {
            window.clearTimeout(this._searchTimeout);
        }

        this._searchTimeout = window.setTimeout(() => {
            this._searchString = "";
        }, 500);

        // add most recent letter to saved search string
        this._searchString += char;
        return this._searchString;
    }

    _onLabelClick() {
        this._comboEl.focus();
    }

    /**
     *
     * @param {FocusEvent} event
     * @returns
     */
    _onComboBlur(event) {
        // do nothing if relatedTarget is contained within listboxEl
        if (this._listboxEl.contains(/** @type {Node} */ event.relatedTarget)) {
            return;
        }

        // select current option and close
        if (this._open) {
            this._selectOption(this._activeIndex);
            this._updateMenuState(false, false);
        }
    }

    _onComboClick() {
        this._updateMenuState(!this._open, false);
    }

    /**
     * @param {KeyboardEvent} event
     */
    _onComboKeyDown(event) {
        const { key } = event;

        const action = getActionFromKey(event, this._open);

        switch (action) {
            case SelectActions.Last:
            case SelectActions.First:
                this._updateMenuState(true);
            // intentional fallthrough
            case SelectActions.Next:
            case SelectActions.Previous:
            case SelectActions.PageUp:
            case SelectActions.PageDown:
                event.preventDefault();
                return this._onOptionChange(
                    this._getUpdatedIndex(this._activeIndex, action),
                );
            case SelectActions.CloseSelect:
                event.preventDefault();
                this._selectOption(this._activeIndex);
            // intentional fallthrough
            case SelectActions.Close:
                event.preventDefault();
                return this._updateMenuState(false);
            case SelectActions.Type:
                return this._onComboType(key);
            case SelectActions.Open:
                event.preventDefault();
                return this._updateMenuState(true);
        }
    }

    /**
     * @param {string} letter
     */
    _onComboType(letter) {
        // open the listbox if it is closed
        this._updateMenuState(true);

        // find the index of the first matching option
        const searchString = this._getSearchString(letter);
        const searchIndex = getIndexByLetter(
            this._options,
            searchString,
            this._activeIndex + 1,
        );

        // if a match was found, go to it
        if (searchIndex >= 0) {
            this._onOptionChange(searchIndex);
        }
        // if no matches, clear the timeout and search string
        else {
            window.clearTimeout(this._searchTimeout);
            this._searchString = "";
        }
    }

    /**
     * @param {number} index
     */
    _onOptionChange(index) {
        if (index < 0 || index >= this._options.length) {
            return;
        }

        // update state
        this._activeIndex = index;

        // update aria-activedescendant
        this._comboEl.setAttribute(
            "aria-activedescendant",
            `${this._idBase}-${index}`,
        );

        // update active option styles
        this._optionElems.forEach((optionEl) => {
            optionEl.classList.remove("js-option-current");
        });
        this._optionElems[index].classList.add("js-option-current");

        // ensure the new option is in view
        this._maintainScrollVisibility();
    }

    /**
     * @param {number} index
     */
    _onOptionClick(index) {
        if (!this._options[index].disabled) {
            this._onOptionChange(index);
            this._selectOption(index);
            this._updateMenuState(false);
        }
    }

    _onOptionMouseDown() {
        // Clicking an option will cause a blur event,
        // but we don't want to perform the default keyboard blur action
        this.ignoreBlur = true;
    }

    /**
     * @param {number} index
     * @param {boolean} publishEvent
     */
    _selectOption(index, publishEvent = true) {
        if (index < 0 || index >= this._options.length) {
            return;
        }

        // update state
        this._activeIndex = index;
        this._selectedIndex = index;

        // update displayed value
        const selected = this._options[index].text;
        this._comboTextEl.innerHTML = selected;

        // update aria-selected
        this._optionElems.forEach((optionEl) => {
            optionEl.setAttribute("aria-selected", "false");
        });
        this._optionElems[index].setAttribute("aria-selected", "true");

        const value = this._options[index].value;
        if (value && this._inputEl.value !== value) {
            this._inputEl.value = value;
            if (publishEvent) {
                let inputEvent = new Event("input", { bubbles: true });
                this._inputEl.dispatchEvent(inputEvent);
                let changeEvent = new Event("change", { bubbles: true });
                this._inputEl.dispatchEvent(changeEvent);
            }
        }
    }

    /**
     * @param {boolean} open
     * @param {boolean} callFocus
     */
    _updateMenuState(open, callFocus = true) {
        if (this._open === open) {
            return;
        }

        // update state
        this._open = open;

        // update aria-expanded and styles
        this._comboEl.setAttribute("aria-expanded", `${open}`);
        if (open) {
            this._el.classList.add("js-open");
        } else {
            this._el.classList.remove("js-open");
        }

        // update activedescendant
        const activeID = open ? `${this._idBase}-${this._activeIndex}` : "";
        this._comboEl.setAttribute("aria-activedescendant", activeID);

        if (activeID === "" && !isElementInView(this._comboEl)) {
            this._comboEl.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }

        // move focus back to the combobox, if needed
        callFocus && this._comboEl.focus();

        if (this._open) {
            this._updateListBoxPosition();
            window.addEventListener("resize", this._onResizeOrScroll);
            window.addEventListener("scroll", this._onResizeOrScroll, true);

            // ensure the new option is in view
            this._maintainScrollVisibility();
        } else {
            window.removeEventListener("resize", this._onResizeOrScroll);
            window.removeEventListener("scroll", this._onResizeOrScroll, true);
        }
    }

    /**
     * Рассчитать положение выпадающего списка на основе положения
     * элемента combobox
     */
    _updateListBoxPosition() {
        const comboRect = this._comboEl.getBoundingClientRect();
        this._listboxEl.style.top = comboRect.top + comboRect.height + "px";
        this._listboxEl.style.left = comboRect.left + "px";
        this._listboxEl.style.width = comboRect.width + "px";
    }

    /**
     * Скорректировать положение выпадающего списка,
     * когда окно или контейнер селектора скролится или изменяет размеры
     * @param {Event} event
     */
    _onResizeOrScroll(event) {
        if (event.target !== this._listboxEl) {
            // this.selectOption(this.activeIndex);
            // this.updateMenuState(false);
            this._updateListBoxPosition();
        }
    }

    /**
     * ensure a given child element is within the parent's visible scroll area
     * if the child is not visible, scroll the parent
     */
    _maintainScrollVisibility() {
        const scrollParent = this._listboxEl;
        const activeElement =
            /** @type {HTMLElement} */ this._optionElems[this._activeIndex];
        if (!activeElement || !isScrollable(scrollParent)) {
            return;
        }

        const { offsetHeight, offsetTop } = activeElement;
        const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent;

        const isAbove = offsetTop < scrollTop;
        const isBelow =
            offsetTop + offsetHeight > scrollTop + parentOffsetHeight;

        if (isAbove) {
            scrollParent.scrollTo(0, offsetTop);
        } else if (isBelow) {
            scrollParent.scrollTo(
                0,
                offsetTop - parentOffsetHeight + offsetHeight,
            );
        }
    }

    /**
     * @param {string} value
     * @returns {number}
     */
    _getOptionIndex(value) {
        return this._options.findIndex((option) => option.value === value);
    }

    /**
     *
     * @param {string} value
     * @param {string} text
     */
    setOptionText(value, text) {
        let index = this._getOptionIndex(value);
        if (index >= 0) {
            this._options[index].text = text;
            this._optionElems[index].innerText = text;
            if (this._selectedIndex === index) {
                this._comboTextEl.innerText = text;
            }
        }
    }

    /**
     *
     * @param {string} value
     */
    selectOption(value) {
        let index = this._getOptionIndex(value);
        this._selectOption(index, false);
    }

    /**
     * get an updated option index after performing an action
     * @param {number} currentIndex
     * @param {number} action
     */
    _getUpdatedIndex(currentIndex, action) {
        const pageSize = 10; // used for pageup/pagedown

        switch (action) {
            case SelectActions.First: {
                const index = this._getFirstNotDisabledIndex();
                return index >= 0 ? index : currentIndex;
            }
            case SelectActions.Last: {
                const index = this._getLastNotDisabledIndex();
                return index >= 0 ? index : currentIndex;
            }
            case SelectActions.Previous:
                return this._getMovedIndex(currentIndex, -1);
            case SelectActions.Next:
                return this._getMovedIndex(currentIndex, 1);
            case SelectActions.PageUp:
                return this._getMovedIndex(currentIndex, -pageSize);
            case SelectActions.PageDown:
                return this._getMovedIndex(currentIndex, pageSize);
            default:
                return currentIndex;
        }
    }

    /**
     * Получить индекс опции, смещенный относительно текущего на заданное количество позиций,
     * пропуская disabled опции
     * @param {number} currentIndex - текущий индекс
     * @param {number} offset - смещение
     * @returns {number}
     */
    _getMovedIndex(currentIndex, offset) {
        let index = currentIndex;
        const dir = Math.sign(offset);
        const stepCount = Math.abs(offset);
        let step = 0;
        while (step < stepCount) {
            index += dir;
            if (index < 0 || index >= this._options.length) {
                return currentIndex;
            }
            if (!this._options[index].disabled) {
                currentIndex = index;
                ++step;
            }
        }
        return currentIndex;
    }

    /**
     * Индекс первой опции, у которой нет аттрибута disabled
     * @returns {number}
     */
    _getFirstNotDisabledIndex() {
        return this._options.findIndex((opt) => !opt.disabled);
    }

    /**
     * Индекс последней опции, у которой нет аттрибута disabled
     * @returns {number}
     */
    _getLastNotDisabledIndex() {
        for (let i = this._options.length - 1; i >= 0; --i) {
            if (!this._options[i].disabled) return i;
        }
        return -1;
    }
}

const customSelectMap = new WeakMap();

/**
 *
 * @param {HTMLInputElement} el
 * @returns {CustomSelect | undefined}
 */
export function getCustomSelectFromElement(el) {
    return customSelectMap.get(el);
}
